import { useState } from 'react';
import { clsx } from 'clsx';
import { Link } from 'react-router-dom';
import { formatDate } from '@/shared/utils/format';
import type { ComingUpItem, CommunityState, LearnedRule } from '../api/communityApi';
import styles from './Community.module.css';

/**
 * The right column while Joe is active: who Joe is (drafts, never sends), the radius chips, the
 * partner profile chips, what Joe has learned (each with Undo), and what is coming up.
 */
export function JoePanel({
  state,
  learned,
  comingUp,
  askJoeTo,
  busy,
  onRadius,
  onToggleCategory,
  onToggleEventType,
  onToggleReferralCheckin,
  onUndo,
}: {
  state: CommunityState;
  learned: LearnedRule[];
  comingUp: ComingUpItem[];
  /** Where "Ask Joe" goes: his open picks report thread, or Tasks when none is open. */
  askJoeTo: string;
  busy: boolean;
  onRadius: (radiusMi: number) => void;
  onToggleCategory: (key: string) => void;
  onToggleEventType: (key: string) => void;
  onToggleReferralCheckin: () => void;
  onUndo: (ruleId: string) => void;
}) {
  const { settings } = state;
  const groups = settings.categoryGroups ?? [{ key: 'profile', label: `${settings.practiceTypeLabel} profile`, options: settings.categoryOptions }];
  const [profileGroup, ...otherGroups] = groups;
  // Collapsed: the profile chips plus anything switched on outside the profile, so nothing on is
  // ever hidden. Expanded: the less common sources for this practice type, by group.
  const otherOnOptions = otherGroups.flatMap((g) => g.options.filter((o) => settings.enabledCategories.includes(o.key)));
  const [showMore, setShowMore] = useState(false);
  const moreCount = otherGroups.reduce((n, g) => n + g.options.length, 0) - otherOnOptions.length;
  const enabledEvents = new Set(settings.enabledEventTypes.map((e) => e.key));
  const eventOptions = settings.eventTypeOptions ?? [];
  const shownEvents = eventOptions.filter((e) => e.inProfile !== false || enabledEvents.has(e.key));
  const moreEvents = eventOptions.filter((e) => !shownEvents.includes(e));
  const [showMoreEvents, setShowMoreEvents] = useState(false);
  const eventChip = (e: { key: string; label: string }) => {
    const on = enabledEvents.has(e.key);
    return (
      <button key={e.key} type="button" className={clsx(styles.chip, on && styles.chipActive)} aria-pressed={on} disabled={busy} onClick={() => onToggleEventType(e.key)}>
        {e.label}
      </button>
    );
  };
  const categoryChip = (c: { key: string; label: string }) => {
    const on = settings.enabledCategories.includes(c.key);
    return (
      <button key={c.key} type="button" className={clsx(styles.chip, on && styles.chipActive)} aria-pressed={on} disabled={busy} onClick={() => onToggleCategory(c.key)}>
        {c.label}
      </button>
    );
  };
  return (
    <aside className={styles.aside} aria-label="Joe, Community Agent">
      <div className={styles.joePanel}>
        <div className={styles.joeHead}>
          <div className={styles.joeAvatar} aria-hidden>
            J
          </div>
          <div>
            <div className={styles.joeName}>Joe</div>
            <div className={styles.joeRole}>Community Agent · drafts, never sends</div>
          </div>
          {!state.joePaused && (
            <Link to={askJoeTo} className={styles.askJoe}>
              Ask Joe
            </Link>
          )}
        </div>
        {state.joePaused && (
          <div className={styles.profileWhy}>Joe is paused at this location by your PracticeNova team: no new picks or drafts until he is back. Everything you planned stays here, and the Monday reminder still comes.</div>
        )}
        <div className={styles.joeBlurb}>
          Joe scans the NPI registry, Google Maps and the calendar feeds your team set up once a month, or whenever you refresh. He suggests; you decide. Nothing goes out without your OK. Every Monday you get a reminder of what you planned for the next two weeks.
        </div>
        <div>
          <div className={styles.panelKicker}>Radius</div>
          <div className={styles.chips}>
            {settings.radiusOptions.map((r) => (
              <button
                key={r}
                type="button"
                className={clsx(styles.chip, settings.radiusMi === r && styles.chipActive)}
                aria-pressed={settings.radiusMi === r}
                disabled={busy}
                onClick={() => onRadius(r)}
              >
                {r} mi
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className={styles.panelKicker}>Looking for ({settings.practiceTypeLabel.toLowerCase()} profile)</div>
          <div className={styles.profileWhy}>{settings.rationale}</div>
          <div className={styles.chips}>
            {profileGroup?.options.map(categoryChip)}
            {!showMore && otherOnOptions.map(categoryChip)}
          </div>
          {moreCount > 0 && (
            <>
              {showMore &&
                otherGroups.map((g) => (
                  <div key={g.key} className={styles.chipGroup}>
                    <div className={styles.chipGroupLabel}>{g.label}</div>
                    <div className={styles.chips}>{g.options.map(categoryChip)}</div>
                  </div>
                ))}
              <button type="button" className={styles.moreToggle} aria-expanded={showMore} onClick={() => setShowMore((v) => !v)}>
                <span className={styles.moreToggleIcon} aria-hidden>{showMore ? '▲' : '▼'}</span>
                {showMore ? 'Fewer categories' : `More categories (${moreCount})`}
              </button>
            </>
          )}
        </div>
        {settings.eventTypeOptions && settings.eventTypeOptions.length > 0 && (
          <div>
            <div className={styles.panelKicker}>Events</div>
            <div className={styles.chips}>{(showMoreEvents ? eventOptions : shownEvents).map(eventChip)}</div>
            {moreEvents.length > 0 && (
              <button type="button" className={styles.moreToggle} aria-expanded={showMoreEvents} onClick={() => setShowMoreEvents((v) => !v)}>
                <span className={styles.moreToggleIcon} aria-hidden>{showMoreEvents ? '▲' : '▼'}</span>
                {showMoreEvents ? 'Fewer event types' : `More event types (${moreEvents.length})`}
              </button>
            )}
            <div className={styles.chipGroupLabel}>
              Events come from public calendars near you: Joe looks for them (downtown, chamber, city, schools, library) and your PracticeNova team approves each one.
              {state.calendarsProposed ? ` ${state.calendarsProposed} calendar${state.calendarsProposed === 1 ? ' is' : 's are'} waiting for approval.` : ''}
            </div>
          </div>
        )}
        <div>
          <div className={styles.panelKicker}>Monthly referral check-in</div>
          <div className={styles.chips}>
            <button
              type="button"
              className={clsx(styles.chip, settings.referralCheckinOn && styles.chipActive)}
              aria-pressed={settings.referralCheckinOn}
              disabled={busy}
              onClick={onToggleReferralCheckin}
            >
              {settings.referralCheckinOn ? 'On: Joe asks each month' : 'Off: Joe does not ask'}
            </button>
          </div>
          <div className={styles.chipGroupLabel}>Counts only, confirmed by you. Nothing is read from your practice software.</div>
        </div>
      </div>

      <div className={styles.joePanel}>
        <div className={styles.panelKicker}>What Joe has learned</div>
        {learned.length === 0 ? (
          <div className={styles.joeBlurb}>Nothing yet. Pass on a pick with a reason and Joe will remember it here.</div>
        ) : (
          learned.slice(0, 6).map((rule) => (
            <div key={rule.id} className={styles.lesson}>
              <span>{rule.label}</span>
              <button type="button" className={styles.linkButton} disabled={busy} onClick={() => onUndo(rule.id)}>
                Undo
              </button>
            </div>
          ))
        )}
      </div>

      <div className={styles.joePanel}>
        <div className={styles.panelKicker}>Coming up</div>
        {comingUp.length === 0 ? (
          <div className={styles.joeBlurb}>Nothing planned yet.</div>
        ) : (
          comingUp.map((item) => (
            <div key={item.id} className={styles.comingRow}>
              <span>{item.label}</span>
              <span className={styles.comingDate}>{formatDate(item.date, 'MMM d')}</span>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}
